import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '../lib/api';
import { resolveLocale } from '../lib/dateFormat';
import { useAuthStore, useUIStore } from '../store/index';
import { Incident, ImpactDomain, Project } from '../types';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';

type SortColumn = 'incident_number' | 'title' | 'status' | 'created_at';

const Incidents: React.FC = () => {
  const { t } = useTranslation();
  // Global UI density (compact/comfortable) — shared with the tasks list
  // via useUIStore, so the sidebar toggle affects both lists at once.
  const { density, language } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const canCreate = !!user && ['client_user', 'operator', 'manager', 'superadmin'].includes(user.role);

  const [searchParams] = useSearchParams();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [domains, setDomains] = useState<ImpactDomain[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') || '');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [titleFilter, setTitleFilter] = useState('');
  // Urgency/priority filters (backend GET /incidents?urgency=1-4&priority=1-4).
  // Stored as string ('' = All) to fit the <select> values, sent as numbers.
  const [urgencyFilter, setUrgencyFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  // Silent, URL-only filters (no visible dropdown) — driven by Home widget
  // "view all" links, e.g. /incidents?reportedByMe=true or ?group=mine.
  const [reportedByMeFilter] = useState<boolean>(() => searchParams.get('reportedByMe') === 'true');
  const [groupMineFilter] = useState<boolean>(() => searchParams.get('group') === 'mine');
  const [slaFilter] = useState<string>(() => {
    const v = searchParams.get('sla');
    return v === 'at_risk' || v === 'breached' ? v : '';
  });

  // Sorting (server-side) and pagination (server-side, real LIMIT/OFFSET —
  // see backend IncidentModel.findAll/countAll).
  const [sortBy, setSortBy] = useState<SortColumn>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    detailed_description: '',
    urgency: 0,
    priority: 0,
    domain_id: '',
    project_id: ''
  });

  const fetchIncidents = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (projectFilter) params.append('project_id', projectFilter);
      if (titleFilter) params.append('title', titleFilter);
      if (urgencyFilter) params.append('urgency', urgencyFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (reportedByMeFilter && user) params.append('reported_by', user.id);
      if (groupMineFilter && (user?.group_ids?.length || user?.group_id)) params.append('assigned_group_id', (user.group_ids?.length ? user.group_ids : [user.group_id as string]).join(','));
      if (slaFilter) params.append('sla', slaFilter);
      params.append('sort', sortBy);
      params.append('order', sortOrder);
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));
      const res = await api.get(`/incidents?${params.toString()}`);
      const data = (res.data as any)?.data;
      const meta = (res.data as any)?.meta;
      setIncidents(Array.isArray(data) ? data : []);
      setTotal(typeof meta?.total === 'number' ? meta.total : 0);
    } catch (err: any) {
      throw err;
    }
  };

  const fetchDomains = async () => {
    try {
      const res = await api.get('/workspace/domains');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) {
        setDomains(data);
      }
    } catch {
      // domains are optional, ignore errors
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) {
        setProjects(data);
      }
    } catch {
      // projects are optional, ignore errors
    }
  };

  // Domains/projects are dropdown options, not affected by the incident
  // list's own filters — fetched once on mount, not on every filter change.
  useEffect(() => {
    fetchDomains();
    fetchProjects();
  }, []);

  // Any filter/sort change starts back at page 1 — a stale page number
  // from a wider result set could otherwise land past the new last page.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, projectFilter, titleFilter, urgencyFilter, priorityFilter, reportedByMeFilter, groupMineFilter, slaFilter, sortBy, sortOrder]);

  useEffect(() => {
    let cancelled = false;
    // Flat debounce on every trigger (not just typing) — simple and a
    // 300ms delay on a dropdown/page change is not perceptible, whereas
    // debouncing only the title keystrokes would need extra state to
    // distinguish the trigger source.
    const timer = setTimeout(() => {
      const load = async () => {
        setLoading(true);
        setError(null);
        try {
          await fetchIncidents();
        } catch (err: any) {
          if (!cancelled) {
            setError(getApiErrorMessage(err, 'Failed to load incidents.'));
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      load();
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, projectFilter, titleFilter, urgencyFilter, priorityFilter, sortBy, sortOrder, page, pageSize]);

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProjectFilter(e.target.value);
  };

  const handleUrgencyFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUrgencyFilter(e.target.value);
  };

  const handlePriorityFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPriorityFilter(e.target.value);
  };

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortArrow = (column: SortColumn) => (
    <svg
      className={`w-3 h-3 inline-block ml-1 transition-transform ${sortBy === column && sortOrder === 'asc' ? 'rotate-180' : ''} ${sortBy === column ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const resetForm = () => {
    setFormData({ title: '', short_description: '', detailed_description: '', urgency: 0, priority: 0, domain_id: '', project_id: projects.find((p) => p.is_default === 1)?.id || '' });
    setCreateError(null);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const payload: Record<string, any> = {
        title: formData.title,
        short_description: formData.short_description,
        detailed_description: formData.detailed_description,
        urgency: formData.urgency,
        priority: formData.priority
      };
      if (formData.domain_id) {
        payload.domain_id = formData.domain_id;
      }
      if (formData.project_id) {
        payload.project_id = formData.project_id;
      }
      await api.post('/incidents', payload);
      // Re-fetch rather than prepending locally — with real pagination now
      // in play, a local prepend would silently desync `total` and could
      // push the current page's last row out without saying so.
      await fetchIncidents();
      setShowCreate(false);
      resetForm();
    } catch (err: any) {
      setCreateError(getApiErrorMessage(err, 'Failed to create incident.'));
    } finally {
      setCreating(false);
    }
  };

  // Display-only compact stars: live preview in the create form and the
  // list table cells (IncidentDetails.renderStars lookalike, non-interactive).
  const renderStaticStars = (value: number) => (
    <span className="inline-flex" title={String(value)}>
      {[1, 2, 3, 4].map((i) => (
        <svg
          key={i}
          className={`w-3.5 h-3.5 ${i <= value ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
          fill={i <= value ? 'currentColor' : 'none'}
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </span>
  );

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      open: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', label: t('incidents.detail.status.open') },
      in_progress: { color: 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30', label: t('incidents.detail.status.inProgress') },
      closed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: t('incidents.detail.status.closed') },
      problem: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: t('incidents.detail.status.problem') }
    };
    const c = config[status] || { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const getSlaBadge = (sla: string | null | undefined) => {
    if (!sla) return null;
    const config: Record<string, { color: string; label: string }> = {
      on_track: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: t('incidents.detail.sla.onTrack') },
      breached: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: t('incidents.detail.sla.breached') },
      pending: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: t('incidents.detail.sla.pending') }
    };
    const c = config[sla] || { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: sla };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('incidents.incidentManagement')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('incidents.list.subtitle')}</p>
        </div>
        {canCreate && (
          <button
            onClick={() => { resetForm(); setShowCreate(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('incidents.createNewIncident')}
          </button>
        )}
      </div>

      {/* Create Form Modal */}
      {showCreate && (
        <Modal>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('incidents.createNewIncident')}</h2>
              <button
                onClick={() => setShowCreate(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-6 space-y-5">
              {createError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {createError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.title')} *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('incidents.list.titlePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.fields.shortDescription')} *</label>
                <input
                  type="text"
                  required
                  value={formData.short_description}
                  onChange={(e) => setFormData((p) => ({ ...p, short_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('incidents.list.shortDescPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.fields.detailedDescription')} *</label>
                <textarea
                  required
                  rows={4}
                  value={formData.detailed_description}
                  onChange={(e) => setFormData((p) => ({ ...p, detailed_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('incidents.list.detailedDescPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.fields.urgency')} *</label>
                  {formData.urgency > 0 && (
                    <div className="mb-2">{renderStaticStars(formData.urgency)}</div>
                  )}
                  <select
                    required
                    value={formData.urgency === 0 ? '' : formData.urgency}
                    onChange={(e) => setFormData((p) => ({ ...p, urgency: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="" disabled>{t('incidents.list.pleaseSelect')}</option>
                    {[1, 2, 3, 4].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  {formData.urgency === 0 && <p className="text-xs text-red-500 mt-1">{t('incidents.list.pleaseSelect')}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.fields.priority')} *</label>
                  {formData.priority > 0 && (
                    <div className="mb-2">{renderStaticStars(formData.priority)}</div>
                  )}
                  <select
                    required
                    value={formData.priority === 0 ? '' : formData.priority}
                    onChange={(e) => setFormData((p) => ({ ...p, priority: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="" disabled>{t('incidents.list.pleaseSelect')}</option>
                    {[1, 2, 3, 4].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  {formData.priority === 0 && <p className="text-xs text-red-500 mt-1">{t('incidents.list.pleaseSelect')}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.list.projectLabel')}</label>
                <select
                  value={formData.project_id}
                  onChange={(e) => setFormData((p) => ({ ...p, project_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.project_key})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('incidents.detail.fields.domain')}</label>
                <select
                  value={formData.domain_id}
                  onChange={(e) => setFormData((p) => ({ ...p, domain_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('incidents.list.selectOptional')}</option>
                  {domains.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors"
                >
                  {t('incidents.detail.actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creating || formData.urgency === 0 || formData.priority === 0}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? t('incidents.list.creating') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Filter Row */}
      <div className="flex items-center gap-3 flex-wrap gap-y-2">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('incidents.list.searchTitleLabel')}</label>
          <input
            type="text"
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            placeholder={t('incidents.list.searchTitlePlaceholder')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('incidents.list.statusFilterLabel')}</label>
          <select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('incidents.list.allOption')}</option>
            <option value="open">{t('incidents.detail.status.open')}</option>
            <option value="in_progress">{t('incidents.detail.status.inProgress')}</option>
            <option value="closed">{t('incidents.detail.status.closed')}</option>
            <option value="problem">{t('incidents.detail.status.problem')}</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('incidents.list.projectFilterLabel')}</label>
          <select
            value={projectFilter}
            onChange={handleProjectFilterChange}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('incidents.list.allOption')}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.project_key})</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('incidents.detail.fields.urgency')}</label>
          <select
            value={urgencyFilter}
            onChange={handleUrgencyFilterChange}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('incidents.list.allOption')}</option>
            {[1, 2, 3, 4].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('incidents.detail.fields.priority')}</label>
          <select
            value={priorityFilter}
            onChange={handlePriorityFilterChange}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('incidents.list.allOption')}</option>
            {[1, 2, 3, 4].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="ml-3 text-gray-600 dark:text-gray-400">{t('incidents.list.loading')}</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('incidents.incidentList')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('incidents.list.listSubtitle')}</p>
          </div>

          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider group cursor-pointer select-none" onClick={() => handleSort('incident_number')}>
                    ID{sortArrow('incident_number')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider group cursor-pointer select-none" onClick={() => handleSort('title')}>
                    {t('common.title')}{sortArrow('title')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider group cursor-pointer select-none" onClick={() => handleSort('status')}>
                    {t('common.status')}{sortArrow('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('incidents.detail.fields.urgency')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('incidents.detail.fields.priority')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">SLA</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider group cursor-pointer select-none" onClick={() => handleSort('created_at')}>
                    {t('common.createdAt')}{sortArrow('created_at')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {incidents.map((inc) => (
                  <tr key={inc.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                    <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap text-sm font-mono text-gray-900 dark:text-gray-100`}>
                      <Link to={`/incidents/${inc.id}`} className="text-blue-600 hover:text-blue-800">
                        {inc.project_key}-{inc.incident_number}
                      </Link>
                    </td>
                    <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} text-sm text-gray-900 dark:text-gray-100 max-w-xs`}>
                      <Link to={`/incidents/${inc.id}`} className="block truncate hover:text-blue-600" title={inc.title}>
                        {inc.title}
                      </Link>
                    </td>
                    <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap`}>
                      {getStatusBadge(inc.status)}
                    </td>
                    <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap`}>
                      {renderStaticStars(inc.urgency)}
                    </td>
                    <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap`}>
                      {renderStaticStars(inc.priority)}
                    </td>
                    <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap`}>
                      {getSlaBadge(inc.sla_status)}
                    </td>
                    <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap text-sm text-gray-500 dark:text-gray-400`}>
                      {formatDate(inc.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden divide-y divide-gray-100 dark:divide-gray-700">
            {incidents.map((inc) => (
              <Link key={inc.id} to={`/incidents/${inc.id}`} className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-900/50">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono text-blue-600">{inc.project_key}-{inc.incident_number}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(inc.created_at)}</span>
                </div>
                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{inc.title}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {getStatusBadge(inc.status)}
                  {getSlaBadge(inc.sla_status)}
                  <span className="flex items-center gap-1 text-xs text-gray-500">{t('incidents.detail.fields.urgency')} {renderStaticStars(inc.urgency)}</span>
                  <span className="flex items-center gap-1 text-xs text-gray-500">{t('incidents.detail.fields.priority')} {renderStaticStars(inc.priority)}</span>
                </div>
              </Link>
            ))}
          </div>

          {total === 0 && (
            <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
              {t('incidents.noIncidents')}
            </div>
          )}

          {total > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Incidents;